import React, { useState, useRef, useEffect } from 'react';
import US_STATES from '../../../utils/usStates';
import './StateAutocomplete.css';

const StateAutocomplete = ({ value, onChange, placeholder = 'City, State', id, name, required }) => {
  const [inputValue, setInputValue] = useState(value);
  const [filteredStates, setFilteredStates] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(e);

    if (val.trim().length > 0) {
      const filtered = US_STATES.filter(
        state =>
          state.name.toLowerCase().includes(val.toLowerCase()) ||
          state.abbreviation.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 8);
      setFilteredStates(filtered);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } else {
      setFilteredStates([]);
      setShowDropdown(false);
    }
  };

  const handleStateSelect = (state) => {
    setInputValue(state.name);
    setShowDropdown(false);
    setFilteredStates([]);
    
    // Create a synthetic event to maintain consistency with onChange
    const event = {
      target: {
        name: name,
        value: state.name,
        type: 'text'
      }
    };
    onChange(event);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredStates.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleStateSelect(filteredStates[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="state-autocomplete-wrapper" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        className="form-control state-autocomplete-input"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (filteredStates.length > 0) {
            setShowDropdown(true);
          }
        }}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      {showDropdown && filteredStates.length > 0 && (
        <div className="state-autocomplete-dropdown">
          {filteredStates.map((state, index) => (
            <div
              key={state.abbreviation}
              className={`state-autocomplete-item ${
                index === highlightedIndex ? 'highlighted' : ''
              }`}
              onClick={() => handleStateSelect(state)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="state-name">{state.name}</span>
              <span className="state-abbreviation">{state.abbreviation}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StateAutocomplete;
